// NTP-style clock-sync estimator. See plan §sync.
//
// Holds a sliding window of the most recent probes. Each probe carries a
// round-trip delay (δ) and an offset estimate (θ). The reported `theta_us`
// is taken from the probe with the smallest δ in the window, smoothed by
// an EWMA against the previous estimate to absorb single-sample jitter.

export interface Probe {
  delta_us: bigint   // round-trip delay
  theta_us: bigint   // (server clock) - (client perf clock), can be negative-ish; stored as bigint i64
}

export interface ClockState {
  theta_us: bigint
  delta_min_us: bigint
  probes_in_window: number
}

const WINDOW = 16
const EWMA_ALPHA = 0.7  // weight on new sample

export class ClockSync {
  private window: Probe[] = []
  private theta: bigint | null = null

  /** Drop everything; used on (re)connect. */
  reset() {
    this.window = []
    this.theta = null
  }

  /**
   * Add a probe outcome and recompute `theta`. T's are microseconds.
   *
   *   T0  client send (perf clock)
   *   T1  server recv (server clock)
   *   T2  server send (server clock)
   *   T3  client recv (perf clock)
   */
  addProbe(T0: bigint, T1: bigint, T2: bigint, T3: bigint) {
    const delta = (T3 - T0) - (T2 - T1)
    const theta = ((T1 - T0) + (T2 - T3)) / 2n

    this.window.push({ delta_us: delta, theta_us: theta })
    if (this.window.length > WINDOW) this.window.shift()

    // NTP-min filter: keep probes within 1.5× of the best δ, then pick the θ
    // of the smallest δ probe.
    let best = this.window[0]
    for (const p of this.window) if (p.delta_us < best.delta_us) best = p

    const cutoff = (best.delta_us * 15n) / 10n
    let chosen = best
    for (const p of this.window) {
      if (p.delta_us <= cutoff && p.delta_us < chosen.delta_us) chosen = p
    }

    // EWMA, but only once we have enough samples to trust it.
    if (this.theta === null || this.window.length < 4) {
      this.theta = chosen.theta_us
    } else {
      const next = (Number(chosen.theta_us) * EWMA_ALPHA) + (Number(this.theta) * (1 - EWMA_ALPHA))
      this.theta = BigInt(Math.round(next))
    }
  }

  state(): ClockState | null {
    if (this.theta === null || this.window.length === 0) return null
    let dmin = this.window[0].delta_us
    for (const p of this.window) if (p.delta_us < dmin) dmin = p.delta_us
    return {
      theta_us: this.theta,
      delta_min_us: dmin,
      probes_in_window: this.window.length,
    }
  }

  /** Convert a server timestamp to a client `performance.now()` timestamp (µs). */
  serverToPerfUs(server_us: bigint): bigint | null {
    if (this.theta === null) return null
    return server_us - this.theta
  }
}
